import { useCommerce } from '../../commerce/ShopCommerce';
import { useMemo } from 'react';
import type { ElementNodeDto } from '@xshop/shared';
import { useElementRender } from './elementRenderContext';
import { resolveBinding, type StoreFacts } from './storeFacts';
import { isStoreDetail } from './storeContact';
import { isBuilderPage } from './ElementPlaceholder';
import { logoDescription } from './storeWordmark';

// Fills a node's bound props from the shop before its renderer ever sees them. No element learns that
// binding exists: a heading bound to the store's name is simply handed the name as its `text`, and the
// eighteen renderers below stay exactly as they were.

// Reports whether a binding answers with a fact the store may leave empty, and did: a detail, or a page's body.
const missingDetail = (source: string, value: unknown): boolean => value === '' && (isStoreDetail(source) || source === 'page.body');

// Writes every answerable binding over what the merchant typed, keeping the typed sample for an empty store detail.
function boundProps(node: ElementNodeDto, facts: StoreFacts | null): Record<string, unknown> {
  const filled: Record<string, unknown> = { ...node.props };
  for (const [key, source] of Object.entries(node.bindings ?? {})) {
    const value = resolveBinding(source, facts);
    if (value !== undefined && !missingDetail(source, value)) filled[key] = value;
  }
  const described = logoDescription(node, facts);
  if (described) filled.alt = described;
  return filled;
}

// Reports whether a node shows a store detail the store left empty, whose sample would read as the store's own.
const lacksStoreDetail = (node: ElementNodeDto, facts: StoreFacts | null): boolean =>
  Object.values(node.bindings ?? {}).some((source) => missingDetail(source, resolveBinding(source, facts)));

/**
 * Resolves one node's bindings against the shop, or hands back the very same node when it has none, or
 * nothing at all for a node a shopper should not see because the store left its detail empty.
 *
 * The identity check matters: all but a handful of nodes on a page are unbound, and returning a fresh
 * object for each of them would re-render the whole tree on every render of its root.
 */
export function useBoundNode(node: ElementNodeDto): ElementNodeDto | null {
  const { store } = useElementRender();
  const commerce = useCommerce();
  return useMemo(() => {
    if (!node.bindings || Object.keys(node.bindings).length === 0) return node;
    const facts = store && commerce ? { ...store, cartCount: commerce.lines.reduce((sum, line) => sum + line.quantity, 0), cartTotal: commerce.quote?.subtotal ?? 0 } : store;
    if (!isBuilderPage() && lacksStoreDetail(node, facts)) return null;
    return { ...node, props: boundProps(node, facts) };
  }, [node, store, commerce]);
}
